import React, { useState } from 'react';

const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'What are bioidentical hormones and how are they different from synthetic hormones?',
      answer: 'Bioidentical hormones are molecularly identical to the hormones naturally produced by your body. Unlike synthetic hormones, which have a different molecular structure, bioidentical hormones are derived from plant sources and modified to match your body\'s natural hormones exactly. This molecular similarity allows for better absorption, fewer side effects, and more natural hormone balance.'
    },
    {
      question: 'How long does it take to see results from hormone optimization therapy?',
      answer: 'Most patients begin to notice improvements within 2-4 weeks of starting treatment. Energy levels and mood often improve first, followed by sleep quality and libido. Full optimization typically occurs within 3-6 months as we fine-tune your dosages based on your response and follow-up lab work.'
    },
    {
      question: 'What are the potential side effects of bioidentical hormone therapy?',
      answer: 'Bioidentical hormones generally have fewer side effects than synthetic alternatives. Possible side effects may include mild acne, breast tenderness, or mood changes during the initial adjustment period. These typically resolve as your body adapts to treatment. Our careful monitoring and dosage adjustments minimize the risk of side effects.'
    },
    {
      question: 'How often will I need follow-up appointments and lab work?',
      answer: 'Initially, we schedule follow-up appointments every 4-6 weeks to monitor your progress and adjust dosages as needed. Once your levels are optimized, appointments typically occur every 3 months. Lab work is performed every 3-6 months to ensure safety and effectiveness of your treatment protocol.'
    },
    {
      question: 'Is hormone optimization therapy covered by insurance?',
      answer: 'Insurance coverage varies by provider and plan. While some aspects of hormone therapy may be covered, many insurance plans consider optimization therapy elective. We provide detailed documentation to help with insurance claims and offer flexible payment options to make treatment accessible.'
    },
    {
      question: 'Can I stop hormone therapy once I feel better?',
      answer: 'Hormone optimization is typically a long-term therapy since hormone decline is a natural part of aging. Stopping treatment will generally result in a return to previous symptoms. However, we work with you to find the lowest effective dose and may adjust your protocol over time based on your needs and preferences.'
    },
    {
      question: 'What delivery methods are available for hormone therapy?',
      answer: 'We offer multiple delivery methods including injections, topical creams and gels, sublingual tablets, and pellet implants. The best method depends on your lifestyle, preferences, and how your body responds to different delivery systems. We\'ll work together to find the most convenient and effective option for you.'
    },
    {
      question: 'Are there any age restrictions for hormone optimization therapy?',
      answer: 'Hormone optimization is most commonly beneficial for adults over 30 who are experiencing symptoms of hormone decline. There\'s no upper age limit, and we\'ve successfully treated patients well into their 80s. We evaluate each patient individually to ensure therapy is safe and appropriate for their specific health profile.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Frequently Asked <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600">Questions</span>
          </h2>
          <p className="text-xl text-gray-600">
            Get answers to common questions about bioidentical hormone optimization therapy.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden">
              <button
                className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                onClick={() => toggleFAQ(index)}
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                <i className={`ri-${openIndex === index ? 'subtract' : 'add'}-line text-2xl text-purple-600 flex-shrink-0`}></i>
              </button>
              
              {openIndex === index && (
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Still Have Questions?
            </h3>
            <p className="text-gray-600 mb-6">
              Our hormone optimization specialists are here to help. Schedule a consultation to discuss your specific concerns.
            </p>
            <button className="bg-gradient-to-r from-pink-600 to-purple-600 text-white px-8 py-4 rounded-full font-semibold hover:from-pink-700 hover:to-purple-700 transition-all duration-300">
              <i className="ri-question-answer-line mr-2"></i>
              Ask Our Experts
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;